import { useEffect } from "react";
import { useGetData } from "@/api/studio/exports/queries";
import { ExportStatusModal } from "@/components/ui/modal";

/* Prop Types */
interface ExportDataModalProps {
  projectId: string;
  studyId: string;
}

/**
 * ExportDataModal
 *
 * Thin wrapper around the generic ExportStatusModal for the data (CSV) export. The
 * underlying endpoint returns the file inline, so there is no generation step to poll —
 * the request resolves straight to a downloadable object URL.
 */
export const ExportDataModal = ({ projectId, studyId }: ExportDataModalProps) => {
  /* Queries */
  const { data, isLoading, isError } = useGetData(projectId, studyId, true);

  /* Derived state */
  const isSuccess = !isLoading && !isError && !!data;

  /* Revoke the object URL when the modal unmounts to avoid leaking it */
  useEffect(() => {
    return () => {
      if (data?.fileUrl) URL.revokeObjectURL(data.fileUrl);
    };
  }, [data?.fileUrl]);

  /* Render */
  return (
    <ExportStatusModal
      isLoading={isLoading}
      isSuccess={isSuccess}
      isError={isError}
      fileUrl={data?.fileUrl}
      downloadFilename={data?.filename}
      loadingTitle="Preparing data export"
      loadingDescription="Please wait while we prepare your data export..."
      successTitle="Data export is ready"
      errorDescription="We couldn't prepare your export, please try again or contact support"
    />
  );
};
